import {
  Breadcrumb,
  Button,
  Card,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BankAccountCreateModal from "./components/BankAccountCreateModal";
import BankAccountEditModal from "./components/BankAccountEditModal";
import { getInstitution } from "../../../api/institutionApi";
import {
  getAccountsForAdmin,
} from "../../../api/bankAccountApi";
import { getBankLabel } from "./components/bankAccountConstants";

const { Title, Text } = Typography;

export default function InstitutionManageAccountsPage() {
  const { institutionId } = useParams();

  const [institution, setInstitution] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const loadPage = async () => {
    try {
      setLoading(true);

      const [institutionData, accountData] = await Promise.all([
        getInstitution(institutionId),
        getAccountsForAdmin(institutionId),
      ]);

      setInstitution(institutionData || null);
      setAccounts(Array.isArray(accountData) ? accountData : []);
    } catch (error) {
      message.error("Мэдээлэл ачааллахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!institutionId) return;
    loadPage();
  }, [institutionId]);

  const bankOptions = useMemo(() => {
    const unique = [...new Set(accounts.map((item) => item.bank).filter(Boolean))];
    return unique.map((item) => ({
      label: getBankLabel(item),
      value: item,
    }));
  }, [accounts]);

  const currencyOptions = useMemo(() => {
    const unique = [
      ...new Set(
        accounts
          .map((item) => item?.currency?.code || item?.currencyCode || item?.currency)
          .filter(Boolean)
      ),
    ];

    return unique.map((item) => ({
      label: item,
      value: item,
    }));
  }, [accounts]);

  const isFiltered =
    searchText.trim() !== "" ||
    selectedBanks.length > 0 ||
    selectedCurrencies.length > 0;

  const filteredAccounts = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    return accounts.filter((item) => {
      const currencyValue =
        item?.currency?.code || item?.currencyCode || item?.currency || "";

      const bankMatch =
        selectedBanks.length === 0 || selectedBanks.includes(item.bank);
      const currencyMatch =
        selectedCurrencies.length === 0 ||
        selectedCurrencies.includes(currencyValue);
      const searchMatch =
        !keyword ||
        String(item.accountNumber || "").toLowerCase().includes(keyword) ||
        String(item.bank || "").toLowerCase().includes(keyword) ||
        currencyValue.toLowerCase().includes(keyword);

      return bankMatch && currencyMatch && searchMatch;
    });
  }, [accounts, searchText, selectedBanks, selectedCurrencies]);

  const openEditModal = (record) => {
    setSelectedAccount(record);
    setEditOpen(true);
  };

  const columns = [
    {
      title: "Банк",
      dataIndex: "bank",
      key: "bank",
      render: (value) => <span>{getBankLabel(value)}</span>,
    },
    {
      title: "Дансны дугаар",
      dataIndex: "accountNumber",
      key: "accountNumber",
      render: (value) => (
        <span className="font-mono text-slate-700">{value || "-"}</span>
      ),
    },
    {
      title: "Валют",
      key: "currency",
      width: 120,
      render: (_, record) => {
        const code =
          record?.currency?.code || record?.currencyCode || record?.currency || "-";
        return <Tag color="blue">{code}</Tag>;
      },
    },
    {
      title: "Төлөв",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (_, record) =>
        record?.is_active || record?.isActive ? (
          <Tag color="green">Идэвхтэй</Tag>
        ) : (
          <Tag>Идэвхгүй</Tag>
        ),
    },
    {
      title: "Үйлдэл",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => openEditModal(record)}
        >
          Засах
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-5">
      {/* Breadcrumb above the card */}
      <Breadcrumb
        items={[
          { title: <Link to="/admin/institutions">Байгуулгууд</Link> },
          { title: institution?.name || "Дэлгэрэнгүй" },
        ]}
      />

      <Card className="rounded-2xl shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Title level={3} className="!mb-1">
              {institution?.name || "Байгууллага"}
            </Title>
            <Text className="text-slate-500">
              {isFiltered
                ? `${filteredAccounts.length} / ${accounts.length} данс`
                : `${accounts.length} данс`}
            </Text>
          </div>

          <Space wrap>
            <Input
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined className="text-slate-400" />}
              placeholder="Данс, банк хайх"
              className="rounded-xl"
              style={{ width: 220 }}
            />
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              value={selectedBanks}
              onChange={setSelectedBanks}
              options={bankOptions}
              placeholder="Банк"
              style={{ minWidth: 180 }}
            />
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              value={selectedCurrencies}
              onChange={setSelectedCurrencies}
              options={currencyOptions}
              placeholder="Валют"
              style={{ minWidth: 140 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={loadPage}
              loading={loading}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              Данс нэмэх
            </Button>
          </Space>
        </div>
      </Card>

      <Card className="rounded-2xl shadow-sm">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredAccounts}
          pagination={{ pageSize: 100, showSizeChanger: false }}
          locale={{
            emptyText: <Empty description="Данс олдсонгүй" />,
          }}
        />
      </Card>

      <BankAccountCreateModal
        open={createOpen}
        institutionId={Number(institutionId)}
        institutionName={institution?.name}
        onClose={() => setCreateOpen(false)}
        onSuccess={loadPage}
      />

      <BankAccountEditModal
        open={editOpen}
        account={selectedAccount}
        institutionName={institution?.name}
        onClose={() => {
          setEditOpen(false);
          setSelectedAccount(null);
        }}
        onSuccess={loadPage}
      />
    </div>
  );
}